/**
 * useDrill — owns GET /api/v1/metrics/{id}/drill (build-spec §5 · S3, CD-7:
 * hooks fetch). Enabled only while drill state is present in the URL. The 200
 * body is a typed union: DrillResponse OR an honest Refusal value (§8) — a 422
 * DrillRejection arrives as the ApiError the panel renders verbatim.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import type { DrillRefusal, DrillResult, Site } from '../types/metrics'
import type { DrillState } from './useScope'
import { ApiError, fetchApi } from './apiFetch'

/** Type guard over the §5 200-status union. */
export function isRefusal(result: DrillResult): result is DrillRefusal {
  return 'refused' in result
}

export function useDrill(
  drill: DrillState | null,
  site: Site,
  level: string
): UseQueryResult<DrillResult, ApiError> {
  const { token } = useAuth()
  const pathJson = drill && drill.path.length ? JSON.stringify(drill.path) : null
  const params = new URLSearchParams({ period: drill?.period ?? '', site, level })
  if (pathJson) params.set('path', pathJson)
  return useQuery<DrillResult, ApiError>({
    queryKey: ['drill', drill?.metricId, drill?.period, site, level, pathJson],
    queryFn: () =>
      fetchApi<DrillResult>(`metrics/${drill?.metricId}/drill?${params.toString()}`, token),
    enabled: token !== null && drill !== null && level !== '',
  })
}
